import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import Sheet from '@mui/joy/Sheet';
import Table from '@mui/joy/Table';
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    useHover,
    useFloating,
    FloatingPortal,
    flip,
    offset,
    size,
} from '@floating-ui/react';
import { detectObjectType } from "@utils/detectObjectType";
import { formatDate } from "@utils/dates";
import { Avatar, Box, LinearProgress, Stack, Tooltip, Typography } from "@mui/joy";
import { objectDefinition } from "@api/schema/supportedObjects";
import { FauxTable, FauxTableRow } from "@components/FauxTable";
import { CheckPermission } from "@components/CheckPermission";



const objectTypes = {
    contact: {
        endpoint: 'contacts',
        singularLabel: 'Contact',
        pluralLabel: 'Contacts',
        // requiredFields
        fields: {
            primary: 'name',
            photo: 'profile_image',
            additional: {
                do_not_contact: {
                    label: 'Communications',
                    transformer: (object) => {
                        return 'DO NOT CONTACT';
                    }
                },
                email: 'Email',
                /*
                phone: {
                    label: 'Phone',
                    transformer: (object) => {
                        let numbers = [];
                        if(!!object.work_phone) {
                            numbers.push({
                                label: 'Work',
                                value: `${object.work_phone}${object.preferred_contact_method == 'work' && ' (Preferred)'}`
                                // preferred: object.preferred_contact_method == 'work',
                                // column: 'work_phone',
                            });
                        }
                        if(!!object.cell_phone) {
                            numbers.push({
                                label: 'Cell',
                                value: `${object.cell_phone}${object.preferred_contact_method == 'cell' && ' (Preferred)'}`
                                // preferred: object.preferred_contact_method == 'cell',
                                // column: 'cell_phone',
                            });
                        }
                        if(!!object.home_phone) {
                            numbers.push({
                                label: 'Home',
                                value: `${object.home_phone}${object.preferred_contact_method == 'home' && ' (Preferred)'}`
                                // preferred: object.preferred_contact_method == 'home',
                                // column: 'home_phone',
                            });
                        }

                        if(!numbers) {
                            return null;
                        }

                        return (
                            <FauxTable>
                                {numbers.map(number => (
                                    <FauxTableRow
                                        key={number.label}
                                        title={number.label}
                                    >
                                        {number.value}
                                    </FauxTableRow>
                                ))}
                            </FauxTable>
                        )
                    }
                },
                */
               /*
                preferred_contact_method: {
                    label: 'Preferred Phone',
                    transformer: (object) => {
                        if(
                            !object.cell_phone
                            && !object.work_phone
                            && !object.home_phone
                        ) {
                            return null;
                        }

                        switch(object.preferred_contact_method) {
                            case 'home': return 'Home';
                            case 'work': return 'Work';
                            case 'cell': return 'Cell';
                        }
                        return 'N/A';
                    }
                },
                */
                cell_phone: {
                    label: 'Cell Phone',
                    transformer: (object) => (<>{`${object.cell_phone}${object.cell_phone && object.preferred_contact_method == 'cell' ? ' (Preferred)' : ''}`}</>)
                },
                work_phone: {
                    label: 'Work Phone',
                    transformer: (object) => (<>{`${object.work_phone}${object.work_phone && object.preferred_contact_method == 'work' ? ' (Preferred)' : ''}`}</>)
                },
                home_phone: {
                    label: 'Home Phone',
                    transformer: (object) => (<>{`${object.home_phone}${object.home_phone && object.preferred_contact_method == 'home' ? ' (Preferred)' : ''}`}</>)
                },
            },
        }
    },
    workplan: {
        endpoint: 'work-plans',
        singularLabel: 'Work plan',
        pluralLabel: 'Work plans',
        fields: {
            primary: 'name',
            photo: null,
            additional: {
                description: 'Description',
                number_tasks: {
                    label: 'Tasks',
                    transformer: (object) => {
                        const progress = object.number_tasks ? (((object.number_tasks - object.number_incomplete_tasks) / object.number_tasks) * 100) : 0;
                        return (
                            <Stack
                              direction="row"
                              justifyContent="flex-start"
                              alignItems="center"
                              spacing={2}
                            //   width={'100%'}
                            >
                                <Box sx={{flex: 'auto', whiteSpace: 'nowrap'}}>
                                    <Tooltip title={'Completed Tasks'}>
                                        <Box sx={{display: 'inline'}}>{object.number_tasks - object.number_incomplete_tasks}</Box>
                                    </Tooltip>
                                    {' of '}
                                    <Tooltip title={'Total Tasks'}>
                                        <Box sx={{display: 'inline'}}>{object.number_tasks}</Box>
                                    </Tooltip>
                                </Box>
                                <Box sx={{flex: '1 1 100%', display: 'flex', justifyContent: 'flex-end'}}>
                                    <LinearProgress determinate value={progress} sx={{backgroundColor: 'var(--joy-palette-background-backdrop)'}} />
                                </Box>
                            </Stack>
                        )
                        // return `${object.number_tasks - object.number_incomplete_tasks} / ${object.number_tasks}`;
                    }
                },
                // number_tasks: '# of Tasks',
                // number_incomplete_tasks: '# of Remaining Tasks',
                workplan_date: {
                    label: 'Date',
                    transformer: (object) => {
                        return formatDate(object.workplan_date);
                    }
                }
            },
        }
    },
    business: {
        endpoint: 'businesses',
        singularLabel: 'Business',
        pluralLabel: 'Businesses',
        fields: {
            primary: 'name',
            photo: null,
            additional: {
                do_not_contact: {
                    label: 'Communications',
                    transformer: (object) => {
                        return 'DO NOT CONTACT';
                    }
                },
                dba_name: 'DBA',
                description: 'Description',
                phone: 'Phone',
                url: {
                    label: 'Website',
                    transformer: (object) => {
                        return (<a href={object.url} target="_blank" className="clickable">Visit Website</a>)
                    }
                },
            },
        }
    },
    property: {
        endpoint: 'properties',
        singularLabel: 'Property',
        pluralLabel: 'Properties',
        fields: {
            primary: 'address',
            photo: null,
            additional: {
                owner: {
                    label: 'Owner',
                    transformer: (object) => {
                        return <Link to={`/${object.owner_type == 'App\\Models\\Business' ? 'businesses' : 'contacts'}/${object.owner_id}`}>{object.owner.name}</Link>
                    }
                },
                property_units: {
                    label: '# of Units',
                    transformer: (object) => {
                        return (object.property_units.length);
                    }
                },
            },
        }
    },
    event: {
        endpoint: 'events',
        singularLabel: 'Event',
        pluralLabel: 'Events',
        fields: {
            primary: 'name',
            photo: null,
            additional: null,
        }
    },
    donation: {
        endpoint: 'donations',
        endpointInclude: 'all',
        singularLabel: 'Donation',
        pluralLabel: 'Donations',
        fields: {
            primary: (object) => {
                /*
                if(object.donor) {
                    return `\$${object.amount} from ${object.donor.name}`;
                }
                */
                return `\$${object.amount} Donation`;
            },
            photo: null,
            additional: {
                donor: {
                    label: 'Donor',
                    transformer: (object) => {
                        return (<Link to={`/${object.donor_type == 'App\\Models\\Business' ? 'businesses' : 'contacts'}/${object.donor_id}`}>
                            {!!object.anonymous && (<>
                                Anonymous
                                <CheckPermission permissions={'view donations'}>
                                {` (${object.donor?.name})`}
                                </CheckPermission>
                            </>)}
                            {!object.anonymous && (<>{object.donor?.name ?? 'N/A'}</>)}
                        </Link>)
                    }
                },
                campaign: {
                    label: 'Campaign',
                    transformer: (object) => {
                        return object.campaign.name
                    }
                },
                fund: {
                    label: 'Fund',
                    transformer: (object) => {
                        return object.fund.name
                    }
                },
                appeal: {
                    label: 'Appeal',
                    transformer: (object) => {
                        return object.appeal.name
                    }
                },
            }
        }
    },
    importer: {
        endpoint: 'importers',
        singularLabel: 'Importer',
        pluralLabel: 'Importers',
        fields: {
            primary: 'name',
            photo: null,
            additional: null,
        }
    },
    opportunity: {
        endpoint: 'opportunities',
        singularLabel: 'Opportunity',
        pluralLabel: 'Opportunities',
        fields: {
            primary: 'name',
            photo: null,
            additional: null,
        }
    },
};

export default function ObjectPreview({
    // TODO: allow JUST passing an object and determining what it is automatically, as well as whether we need more information
    object:_obj,

    // for linking
    // to,
    // relative,

    type = null,

    children,

    ...restProps
}) {
    const [object, setObject] = useState(_obj);
    const [isOpen, setIsOpen] = useState(false);
    const [maxWidth, setMaxWidth] = useState(null);
    // const [objectType, setObjectType] = useState(null);

    const objectType = type ?? detectObjectType(object);

    /*
    useEffect(() => {
      setObjectType(detectObjectType(object));
    }, [object]);
    */

    /*
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [object, setObject] = useState(null);
    */

    // objectTypes.hasOwnProperty(props.type)

    const {refs, floatingStyles, context} = useFloating({
        placement: 'bottom-start', // right
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [
            offset(10),
            flip(),
            size({
                apply({availableWidth}) {
                    flushSync(() => setMaxWidth(Math.min(availableWidth, 400)));
                },
            }),

            // shift(),
        ],
        // whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context, {
        delay: {
            open: 500,
            close: 250,
        },
    });
    // const click = useClick(context);
    const dismiss = useDismiss(context);
    // const role = useRole(context);

    // Merge all the interactions into prop getters
    const {getReferenceProps, getFloatingProps} = useInteractions([
        // click,
        hover,
        dismiss,
        // role,
    ]);

    if(!objectType) {
        return <Link {...restProps}>{children}</Link>
    }

    const objectFields = objectTypes[objectType].fields;

    let additionalFields = {};
    let hasAdditionalFields = false;
    if(objectFields.additional !== null) {
        for(const [k,v] of Object.entries(objectFields.additional)) {
            if(object[k]) {
                if(typeof v === 'object') {
                    additionalFields[v.label] = v.transformer(object);
                } else {
                    additionalFields[v] = object[k];
                }

                hasAdditionalFields = true;
            }
        }
    }

    const headerThPadding = objectFields.photo !== null && object[objectFields.photo] ? {pt: 1, pb: 1} : {pt: 4, pb: 4};

    let customTint = 'primary';//.600';
    try {
        const definition = objectDefinition(objectType);
        customTint = `${definition.color}`;//.600';
    } catch(e) {
        //
    }

    return (<>
        <Box
            component={'span'}
            ref={refs.setReference}
            {...getReferenceProps()}
            // style={{display: 'inline-block'}}
            sx={{
                display: 'inline-block',
                'a': {
                    color: `${customTint}.600`,
                    // textDecoration: 'underline',
                },
                /*
                'a > p':{
                    textDecoration: 'underline',
                },
                */
                'a:hover': {
                    color: `${customTint}.900`,
                }
            }}
        >
            <Link {...restProps}>{children}</Link>
        </Box>
        {isOpen && (
            <FloatingPortal>
                <div
                    ref={refs.setFloating}
                    style={{...floatingStyles, maxWidth, zIndex: 'var(--joy-zIndex-popup)'}}
                    {...getFloatingProps()}
                >

                    <Sheet
                        variant="outlined"
                        sx={theme => ({
                            // display: { xs: 'none', sm: 'initial' },
                            // width: '100%',
                            borderRadius: 'sm',
                            // flexShrink: 1,
                            // overflow: 'auto',
                            // minHeight: 0,
                            // m: 2,
                            backgroundColor: theme.palette.background,
                        })}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            // stickyHeader
                            // hoverRow
                            stripe={'even'}
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableRow-stripeBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>{/* variant={props.type} */}
                                <tr>
                                    <th colSpan={2} {...headerThPadding}>
                                        <Stack
                                            direction={'row'}
                                            alignItems={'center'}
                                            gap={1}
                                        >
                                            {!!object?.[objectFields?.photo] && (
                                                <Avatar verticalAlign={'middle'} mr={4} src={object[objectFields.photo].url} />
                                            )}

                                            {typeof objectFields.primary === 'string' && object[objectFields.primary]}
                                            {typeof objectFields.primary === 'function' && objectFields.primary(object)}
                                        </Stack>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {!hasAdditionalFields && (
                                    <tr>
                                        <td colSpan={2}>
                                            <Typography fontSize={'sm'} fontStyle={'italic'}>There is no additional information to display.</Typography>
                                        </td>
                                    </tr>
                                )}
                                {Object.entries(additionalFields).map(([k,v]) => {
                                    if(!v) return null;
                                    return (
                                        <tr key={k}>
                                            <td><b>{k}</b></td>
                                            <td>{v}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Sheet>
                </div>
            </FloatingPortal>
        )}
    </>);
}
